// Engine-parameterized in-buffer CREATE TABLE scanner — surfaces tables
// declared earlier in the same buffer so subsequent statements can reference
// them and their columns even before the schema-bus snapshot has refreshed.
//
// The schema cache only refreshes after the DDL is committed to the live
// database; users expect autocomplete to reflect what they have just typed.
// Closes goals/autocomplete-coverage in-buffer-CREATE gaps across engines
// (pgsql script 42, mysql scripts 40/41/44, …).
//
// Scope: COMPLETED CREATE TABLE statements that end strictly BEFORE the
// cursor offset. Intra-statement column surfacing (e.g. a `PARTITION BY`
// clause inside the CREATE TABLE itself) is handled by a separate slot
// detector — the statement isn't terminated yet, so it's skipped here.
//
// `qualifier` is the single name segment before the table name (mysql:
// database, pgsql/oracle: schema). Multi-part names (mssql `db.schema.table`)
// collapse to the segment immediately preceding the table; engines that need
// the full path map it at the call site.

import type { DatabaseEngineId } from '../../../core/engines';
import { tokenize, type Token } from './tokenizer';

export type InBufferTable = {
  readonly name: string;
  readonly qualifier: string | null;
  readonly isTemporary: boolean;
  readonly columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>;
};

const TABLE_LEVEL_CLAUSE_KEYWORDS = new Set([
  'PRIMARY',
  'UNIQUE',
  'KEY',
  'INDEX',
  'FOREIGN',
  'CONSTRAINT',
  'FULLTEXT',
  'SPATIAL',
  'CHECK',
  'EXCLUDE',
  'LIKE',
]);

export function findInBufferCreatedTables(
  source: string,
  cursorOffset: number,
  engineId: DatabaseEngineId,
): InBufferTable[] {
  // Tokenize the slice up to the cursor; comments / strings are skipped by
  // the tokenizer so embedded `CREATE TABLE` mentions inside comments or
  // dollar-quoted bodies are invisible to us.
  const tokens = tokenize(source, 0, cursorOffset, engineId);
  const out: InBufferTable[] = [];

  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i]!;
    if (identUpper(tok) !== 'CREATE') {
      i += 1;
      continue;
    }
    // Skip CREATE
    let j = i + 1;
    // Optional global/local + temp + virtual modifiers (`CREATE GLOBAL
    // TEMPORARY TABLE`, `CREATE TEMP TABLE`, `CREATE UNLOGGED TABLE`,
    // SQLite `CREATE VIRTUAL TABLE … USING module(...)`).
    while (
      identUpper(tokens[j]) === 'GLOBAL' ||
      identUpper(tokens[j]) === 'LOCAL' ||
      identUpper(tokens[j]) === 'UNLOGGED' ||
      identUpper(tokens[j]) === 'VIRTUAL'
    ) {
      j += 1;
    }
    let isTemporary = false;
    if (identUpper(tokens[j]) === 'TEMPORARY' || identUpper(tokens[j]) === 'TEMP') {
      isTemporary = true;
      j += 1;
    }
    if (identUpper(tokens[j]) !== 'TABLE') {
      // Not a CREATE TABLE; continue scanning.
      i = j;
      continue;
    }
    j += 1;
    // Optional `IF NOT EXISTS`
    if (
      identUpper(tokens[j]) === 'IF' &&
      identUpper(tokens[j + 1]) === 'NOT' &&
      identUpper(tokens[j + 2]) === 'EXISTS'
    ) {
      j += 3;
    }
    // Table name (possibly `qualifier.name` or `db.schema.name`). Walk the
    // dotted chain; the last segment is the table name, the one before it is
    // the qualifier we surface.
    const nameTok = tokens[j];
    if (!nameTok || (nameTok.type !== 'ident' && nameTok.type !== 'kw')) {
      i = j;
      continue;
    }
    let qualifier: string | null = null;
    let tableName = unquoteIdent(nameTok.text);
    j += 1;
    while (tokens[j]?.type === 'dot') {
      const next = tokens[j + 1];
      if (next && (next.type === 'ident' || next.type === 'kw')) {
        qualifier = tableName;
        tableName = unquoteIdent(next.text);
        j += 2;
      } else {
        break;
      }
    }
    // SQLite virtual tables: `… USING <module> ( <args> )`. The module's
    // arguments are the queryable column names (e.g. rtree(id, min_x, …)),
    // so skip `USING <module>` and parse the paren group like a column list.
    if (identUpper(tokens[j]) === 'USING') {
      j += 1;
      if (tokens[j]?.type === 'ident' || tokens[j]?.type === 'kw') j += 1;
    }
    // Expect `(`
    if (tokens[j]?.type !== 'lparen') {
      i = j;
      continue;
    }
    const lparenIdx = j;
    // Find matching `)` at depth 0.
    const rparenIdx = findMatchingRParen(tokens, lparenIdx);
    if (rparenIdx === -1) {
      // Unbalanced — the CREATE TABLE extends past the cursor; skip.
      i = j + 1;
      continue;
    }
    // Find the closing `;` after the `)` (and any trailing options).
    const semiIdx = findSemicolonAfter(tokens, rparenIdx + 1);
    if (semiIdx === -1) {
      // Statement not yet terminated → it's the current statement; skip.
      i = rparenIdx + 1;
      continue;
    }
    const columns = parseColumnList(tokens, lparenIdx + 1, rparenIdx);
    out.push({
      name: tableName,
      qualifier,
      isTemporary,
      columns,
    });
    i = semiIdx + 1;
  }
  return out;
}

// ───────────────────────────────────────────────────────────────────────────
// In-buffer CREATE VIEW — surfaces a view declared earlier in the same buffer
// so a later statement can reference it AND its projected columns before the
// schema-bus snapshot refreshes. Unlike a table, a view has no column list of
// its own: its columns are the SELECT-body projection (or an explicit
// `(col, …)` list), so we capture the body offset range and let the resolver
// infer the columns via `inferSubqueryColumns`.
// ───────────────────────────────────────────────────────────────────────────

export type InBufferView = {
  readonly name: string;
  readonly qualifier: string | null;
  /** Absolute offset of the view's SELECT body (the token after `AS`). */
  readonly bodyStart: number;
  /** Absolute offset just before the terminating `;`. */
  readonly bodyEnd: number;
  /** Explicit `CREATE VIEW v (a, b) AS …` column list, when present — these
   *  ARE the view's columns and take precedence over body inference. */
  readonly explicitColumns?: readonly string[];
  /**
   * What the statement actually created. `CREATE TABLE t AS SELECT …` (CTAS)
   * has the same shape as a view for completion purposes — the columns are a
   * projection, not a declared list — and is scanned by the same pass, but it
   * materialises a TABLE and must be surfaced as one (kind drives the popup's
   * icon and the table/view ranking split). Defaults to `'view'`.
   */
  readonly relationKind?: 'view' | 'table';
};

export function findInBufferCreatedViews(
  source: string,
  cursorOffset: number,
  engineId: DatabaseEngineId,
): InBufferView[] {
  const tokens = tokenize(source, 0, cursorOffset, engineId);
  const out: InBufferView[] = [];
  let i = 0;
  while (i < tokens.length) {
    if (identUpper(tokens[i]) !== 'CREATE') { i += 1; continue; }
    let j = i + 1;
    if (identUpper(tokens[j]) === 'OR' && identUpper(tokens[j + 1]) === 'REPLACE') j += 2;
    // Skip mysql view-prefix options (`ALGORITHM = …`, `DEFINER = …`,
    // `SQL SECURITY …`, `TEMPORARY`) until the `VIEW` keyword — bounded so a
    // non-view CREATE bails quickly.
    let guard = 0;
    while (
      j < tokens.length &&
      identUpper(tokens[j]) !== 'VIEW' &&
      identUpper(tokens[j]) !== 'TABLE' &&
      guard < 16
    ) {
      const u = identUpper(tokens[j]);
      if (tokens[j]!.type === 'lparen' || tokens[j]!.type === 'semicolon' || u === 'AS' || u === 'SELECT') break;
      j += 1; guard += 1;
    }
    // `CREATE TABLE <name> AS <select>` (CTAS) reaches the same body scan: its
    // columns are a projection, so the declared-column-list scanner
    // (`findInBufferCreatedTables`) skips it — it requires a `(` after the
    // name — and without this pass a later `alias.▮` against the new table
    // resolved to nothing (sqlite script.49 §8/§9/§10).
    const relationKind: 'view' | 'table' =
      identUpper(tokens[j]) === 'TABLE' ? 'table' : 'view';
    if (identUpper(tokens[j]) !== 'VIEW' && identUpper(tokens[j]) !== 'TABLE') {
      i = j + 1;
      continue;
    }
    j += 1;
    // `CREATE TABLE IF NOT EXISTS x AS …`.
    if (
      relationKind === 'table' &&
      identUpper(tokens[j]) === 'IF' &&
      identUpper(tokens[j + 1]) === 'NOT' &&
      identUpper(tokens[j + 2]) === 'EXISTS'
    ) {
      j += 3;
    }
    const ref = readDottedRef(tokens, j);
    if (!ref) { i = j + 1; continue; }
    j = ref.nextIdx;
    // Optional explicit column list `(a, b, …)`.
    let explicitColumns: readonly string[] | undefined;
    if (tokens[j]?.type === 'lparen') {
      const close = findMatchingRParen(tokens, j);
      if (close !== -1) {
        const cols = readIdentList(tokens, j, close);
        if (cols.length > 0) explicitColumns = cols;
        j = close + 1;
      }
    }
    // A CTAS REQUIRES the `AS`; a `CREATE TABLE t (…)` has no body and is the
    // declared-column-list scanner's business, so bail rather than mis-read
    // its trailing options as a projection.
    if (identUpper(tokens[j]) === 'AS') j += 1;
    else if (relationKind === 'table') { i = j + 1; continue; }
    const bodyTok = tokens[j];
    if (!bodyTok) { i = j + 1; continue; }
    const semiIdx = findSemicolonAfter(tokens, j);
    // Not terminated before the cursor → it's the statement being typed; skip
    // (don't surface a view from its own still-open definition).
    if (semiIdx === -1) { i = j + 1; continue; }
    out.push({
      name: ref.name,
      qualifier: ref.qualifier,
      bodyStart: bodyTok.start,
      bodyEnd: tokens[semiIdx]!.start,
      ...(explicitColumns ? { explicitColumns } : {}),
      relationKind,
    });
    i = semiIdx + 1;
  }
  return out;
}

/** Read a comma-separated identifier list between `(` and `)` (column list). */
function readIdentList(
  tokens: ReadonlyArray<Token>,
  lparenIdx: number,
  rparenIdx: number,
): string[] {
  const cols: string[] = [];
  let expectIdent = true;
  for (let k = lparenIdx + 1; k < rparenIdx; k += 1) {
    const t = tokens[k]!;
    if (expectIdent) {
      if (t.type !== 'ident' && t.type !== 'kw') return cols;
      cols.push(unquoteIdent(t.text));
      expectIdent = false;
    } else {
      if (t.type !== 'comma') return cols;
      expectIdent = true;
    }
  }
  return cols;
}

// ───────────────────────────────────────────────────────────────────────────
// In-buffer table OPS — rename / drop. CREATE is surfaced by
// `findInBufferCreatedTables` above (it ADDS rows); these verbs MUTATE the
// known set: a rename retires the old name and introduces the new one, a drop
// retires the table. All position-bounded — only DDL that ends before the
// cursor is considered, so completion reflects the buffer state at the caret.
// ───────────────────────────────────────────────────────────────────────────

export type InBufferRename = {
  readonly kind: 'rename';
  readonly fromQualifier: string | null;
  readonly fromName: string;
  readonly toQualifier: string | null;
  readonly toName: string;
};
export type InBufferDrop = {
  readonly kind: 'drop';
  readonly qualifier: string | null;
  readonly name: string;
};
export type InBufferTableOp = InBufferRename | InBufferDrop;

const NULL_REF = { qualifier: null as string | null, name: '' };

export function scanInBufferTableOps(
  source: string,
  cursorOffset: number,
  engineId: DatabaseEngineId,
): InBufferTableOp[] {
  const tokens = tokenize(source, 0, cursorOffset, engineId);
  const ops: InBufferTableOp[] = [];
  // Only DDL in a statement TERMINATED before the cursor counts — the final,
  // still-being-typed statement (everything after the last `;`) must not emit
  // ops (e.g. a half-typed `DROP TABLE dbo.|` is not a committed drop). A
  // statement is terminated iff its leading keyword sits before the last
  // semicolon token.
  let lastSemiIdx = -1;
  for (let k = tokens.length - 1; k >= 0; k -= 1) {
    if (tokens[k]!.type === 'semicolon') {
      lastSemiIdx = k;
      break;
    }
  }
  let i = 0;
  while (i < tokens.length) {
    if (i >= lastSemiIdx) break; // inside the in-progress final statement
    const word = identUpper(tokens[i]);
    if (word === 'DROP') {
      i = scanDrop(tokens, i, ops);
      continue;
    }
    if (word === 'ALTER') {
      i = scanAlterRename(tokens, i, ops);
      continue;
    }
    if (word === 'RENAME') {
      // MySQL standalone `RENAME TABLE a TO b[, c TO d]`. (`ALTER … RENAME TO`
      // is handled in scanAlterRename; a bare `RENAME` not followed by `TABLE`
      // is the ALTER tail and was already consumed there.)
      i = scanRenameTable(tokens, i, ops);
      continue;
    }
    if (word === 'SP_RENAME') {
      i = scanSpRename(tokens, i, ops);
      continue;
    }
    i += 1;
  }
  return ops;
}

/**
 * Apply rename/drop ops to a table set (snapshot ∪ in-buffer-created). Generic
 * over the engine's table row so all five resolvers share the merge logic:
 * `keyOf` folds a row to its dedup key, `keyOfRef` folds a (qualifier, name)
 * pair the same way, and `renameTable` builds the renamed row (inheriting the
 * source row's columns/kind when it existed). Ops are applied in source order.
 */
export function applyInBufferTableOps<T>(
  tables: ReadonlyArray<T>,
  ops: ReadonlyArray<InBufferTableOp>,
  helpers: {
    readonly keyOf: (row: T) => string;
    readonly keyOfRef: (qualifier: string | null, name: string) => string;
    readonly renameTable: (existing: T | undefined, op: InBufferRename) => T;
  },
): T[] {
  if (ops.length === 0) return [...tables];
  let arr: T[] = [...tables];
  for (const op of ops) {
    if (op.kind === 'drop') {
      const k = helpers.keyOfRef(op.qualifier, op.name);
      arr = arr.filter((row) => helpers.keyOf(row) !== k);
      continue;
    }
    const fromKey = helpers.keyOfRef(op.fromQualifier, op.fromName);
    const toKey = helpers.keyOfRef(op.toQualifier, op.toName);
    const existing = arr.find((row) => helpers.keyOf(row) === fromKey);
    // Retire the old name AND any pre-existing row colliding with the new name,
    // then introduce the renamed row.
    arr = arr.filter((row) => {
      const k = helpers.keyOf(row);
      return k !== fromKey && k !== toKey;
    });
    arr.push(helpers.renameTable(existing, op));
  }
  return arr;
}

/**
 * Columns an `ALTER TABLE … ADD [COLUMN] <name> <type>` in the buffer adds to
 * an EXISTING table, keyed `<qualifier>|<name>` (both folded; the qualifier
 * segment is empty when the statement wrote the table unqualified).
 *
 * `findInBufferCreatedTables` covers the table that does not exist yet;
 * this covers the column that does not exist yet. A script that extends a
 * table and then references the new column further down (pgsql coverage
 * script.30 §4 — `ADD COLUMN film_id …` then `FOREIGN KEY (film_id)`) got
 * nothing at the second caret, because the snapshot only knows the columns
 * the table had when the connection warmed.
 *
 * Position-bounded like `scanInBufferTableOps`: only a statement TERMINATED
 * before the cursor counts, so the half-typed `ADD COLUMN fo▮` the user is
 * still writing does not register a column named `fo`.
 *
 * Multiple actions in one ALTER (`ADD COLUMN a INT, ADD COLUMN b TEXT`) are
 * all collected; every non-ADD action (`DROP COLUMN`, `ADD CONSTRAINT`, `ALTER
 * COLUMN`) is skipped, as is `ADD` followed by a table-level constraint word.
 */
export function findInBufferAddedColumns(
  source: string,
  cursorOffset: number,
  engineId: DatabaseEngineId,
): Map<string, ReadonlyArray<{ readonly name: string; readonly dataType: string }>> {
  const tokens = tokenize(source, 0, cursorOffset, engineId);
  const out = new Map<string, { name: string; dataType: string }[]>();
  let lastSemiIdx = -1;
  for (let k = tokens.length - 1; k >= 0; k -= 1) {
    if (tokens[k]!.type === 'semicolon') {
      lastSemiIdx = k;
      break;
    }
  }
  let i = 0;
  while (i < tokens.length) {
    if (i >= lastSemiIdx) break;
    if (identUpper(tokens[i]) !== 'ALTER' || identUpper(tokens[i + 1]) !== 'TABLE') {
      i += 1;
      continue;
    }
    let j = i + 2;
    if (identUpper(tokens[j]) === 'IF' && identUpper(tokens[j + 1]) === 'EXISTS') j += 2;
    if (identUpper(tokens[j]) === 'ONLY') j += 1;
    const ref = readDottedRef(tokens, j);
    if (!ref) {
      i = j + 1;
      continue;
    }
    j = ref.nextIdx;
    const stmtEnd = findSemicolonAfter(tokens, j);
    const end = stmtEnd === -1 ? tokens.length : stmtEnd;
    const key = `${(ref.qualifier ?? '').toLowerCase()}|${ref.name.toLowerCase()}`;
    // Walk the action list at depth 0, splitting on commas.
    let depth = 0;
    let actionStart = j;
    for (let k = j; k <= end; k += 1) {
      const t = tokens[k];
      if (t && t.type === 'lparen') { depth += 1; continue; }
      if (t && t.type === 'rparen') { depth -= 1; continue; }
      const atBreak = k === end || (depth === 0 && t?.type === 'comma');
      if (!atBreak) continue;
      collectAddedColumn(tokens, actionStart, k, out, key);
      actionStart = k + 1;
    }
    i = end;
  }
  return out;
}

/** Read one ALTER action in `[start, end)`; record it when it ADDs a column. */
function collectAddedColumn(
  tokens: ReadonlyArray<Token>,
  start: number,
  end: number,
  out: Map<string, { name: string; dataType: string }[]>,
  key: string,
): void {
  let j = start;
  if (identUpper(tokens[j]) !== 'ADD') return;
  j += 1;
  if (identUpper(tokens[j]) === 'COLUMN') j += 1;
  if (identUpper(tokens[j]) === 'IF' && identUpper(tokens[j + 1]) === 'NOT'
    && identUpper(tokens[j + 2]) === 'EXISTS') j += 3;
  const nameTok = tokens[j];
  if (!nameTok || (nameTok.type !== 'ident' && nameTok.type !== 'kw')) return;
  const nameUpper = identUpper(nameTok)!;
  // `ADD PRIMARY KEY (…)` / `ADD CONSTRAINT …` / `ADD UNIQUE (…)` — a
  // table-level constraint, not a column.
  if (TABLE_LEVEL_CLAUSE_KEYWORDS.has(nameUpper) || nameUpper === 'CONSTRAINT') return;
  // A type is mandatory for a column addition; without one this is a
  // half-typed action, not a committed column.
  const typeStart = j + 1;
  if (typeStart >= end) return;
  const dataType = extractDataType(tokens, typeStart, end);
  if (!dataType) return;
  const list = out.get(key) ?? [];
  if (list.some((c) => c.name.toLowerCase() === unquoteIdent(nameTok.text).toLowerCase())) return;
  list.push({ name: unquoteIdent(nameTok.text), dataType });
  out.set(key, list);
}

function scanDrop(tokens: ReadonlyArray<Token>, i: number, ops: InBufferTableOp[]): number {
  let j = i + 1;
  if (identUpper(tokens[j]) !== 'TABLE') return i + 1; // DROP VIEW/PROC/… — not a table
  j += 1;
  if (identUpper(tokens[j]) === 'IF' && identUpper(tokens[j + 1]) === 'EXISTS') j += 2;
  const first = readDottedRef(tokens, j);
  if (!first) return j + 1;
  // One or more comma-separated targets — `DROP TABLE a, b, c` (valid PG/MySQL/
  // SQLite multi-table form). Each named table must produce its own drop op.
  ops.push({ kind: 'drop', qualifier: first.qualifier, name: first.name });
  j = first.nextIdx;
  while (tokens[j]?.type === 'comma') {
    j += 1;
    const next = readDottedRef(tokens, j);
    if (!next) break;
    ops.push({ kind: 'drop', qualifier: next.qualifier, name: next.name });
    j = next.nextIdx;
  }
  return j;
}

function scanAlterRename(tokens: ReadonlyArray<Token>, i: number, ops: InBufferTableOp[]): number {
  let j = i + 1;
  if (identUpper(tokens[j]) !== 'TABLE') return i + 1;
  j += 1;
  if (identUpper(tokens[j]) === 'IF' && identUpper(tokens[j + 1]) === 'EXISTS') j += 2;
  const from = readDottedRef(tokens, j);
  if (!from) return j + 1;
  j = from.nextIdx;
  // Only a RENAME immediately after the table name is a table rename. Any other
  // ALTER clause (ADD/DROP/ALTER COLUMN …) is irrelevant to table existence.
  if (identUpper(tokens[j]) !== 'RENAME') return j;
  j += 1;
  const after = identUpper(tokens[j]);
  if (after === 'COLUMN') return j; // column rename — not a table op
  if (after === 'TO' || after === 'AS') j += 1; // `RENAME TO y` / MySQL `RENAME AS y`
  const to = readDottedRef(tokens, j);
  if (!to) return j;
  ops.push({
    kind: 'rename',
    fromQualifier: from.qualifier,
    fromName: from.name,
    toQualifier: to.qualifier ?? from.qualifier,
    toName: to.name,
  });
  return to.nextIdx;
}

function scanRenameTable(tokens: ReadonlyArray<Token>, i: number, ops: InBufferTableOp[]): number {
  let j = i + 1;
  if (identUpper(tokens[j]) !== 'TABLE') return i + 1;
  j += 1;
  // One or more `old TO new` pairs separated by commas.
  for (;;) {
    const from = readDottedRef(tokens, j);
    if (!from) break;
    j = from.nextIdx;
    if (identUpper(tokens[j]) !== 'TO') break;
    j += 1;
    const to = readDottedRef(tokens, j);
    if (!to) break;
    j = to.nextIdx;
    ops.push({
      kind: 'rename',
      fromQualifier: from.qualifier,
      fromName: from.name,
      toQualifier: to.qualifier ?? from.qualifier,
      toName: to.name,
    });
    if (tokens[j]?.type === 'comma') {
      j += 1;
      continue;
    }
    break;
  }
  return j;
}

function scanSpRename(tokens: ReadonlyArray<Token>, i: number, ops: InBufferTableOp[]): number {
  // `EXEC sp_rename N'dbo.old', N'new'[, N'OBJECT']` — args are string literals.
  // The optional `N` unicode prefix tokenizes as a bare `ident` before the
  // literal; commas separate args. Collect up to three literal values.
  let j = i + 1;
  const literals: string[] = [];
  while (j < tokens.length && tokens[j]!.type !== 'semicolon' && literals.length < 3) {
    const t = tokens[j]!;
    if (t.type === 'literal') {
      literals.push(stripStringLiteral(t.text));
      j += 1;
      continue;
    }
    if (t.type === 'comma' || (t.type === 'ident' && identUpper(t) === 'N')) {
      j += 1;
      continue;
    }
    break; // any other token ends the arg list
  }
  if (literals.length >= 2) {
    const objType = literals[2]?.toUpperCase();
    // Only an OBJECT rename (or the default, no third arg) renames a table;
    // 'COLUMN' / 'INDEX' / 'USERDATATYPE' etc. do not.
    if (!objType || objType === 'OBJECT') {
      const fromRef = splitDottedName(literals[0]!);
      ops.push({
        kind: 'rename',
        fromQualifier: fromRef.qualifier,
        fromName: fromRef.name,
        // sp_rename keeps the object in its schema; the new name is unqualified.
        toQualifier: fromRef.qualifier,
        toName: literals[1]!,
      });
    }
  }
  return j > i ? j : i + 1;
}

/** Read a possibly-dotted object reference (`a`, `a.b`, `db.schema.tbl`) →
 *  the last segment is the name, the one before it the qualifier. */
function readDottedRef(
  tokens: ReadonlyArray<Token>,
  start: number,
): { qualifier: string | null; name: string; nextIdx: number } | null {
  const first = tokens[start];
  if (!first || (first.type !== 'ident' && first.type !== 'kw')) return null;
  let qualifier: string | null = null;
  let name = unquoteIdent(first.text);
  let j = start + 1;
  while (tokens[j]?.type === 'dot') {
    const next = tokens[j + 1];
    if (next && (next.type === 'ident' || next.type === 'kw')) {
      qualifier = name;
      name = unquoteIdent(next.text);
      j += 2;
    } else {
      break;
    }
  }
  return { qualifier, name, nextIdx: j };
}

/** Split a textual dotted name (`dbo.acact_ScratchTable`) into qualifier+name. */
function splitDottedName(raw: string): { qualifier: string | null; name: string } {
  const parts = raw.split('.').map((p) => unquoteIdent(p.trim()));
  if (parts.length === 0 || parts[parts.length - 1] === '') return NULL_REF;
  const name = parts[parts.length - 1]!;
  const qualifier = parts.length >= 2 ? parts[parts.length - 2]! : null;
  return { qualifier, name };
}

/** Strip the surrounding single quotes of a literal token, collapsing `''`. */
function stripStringLiteral(text: string): string {
  if (text.length >= 2 && text[0] === "'" && text[text.length - 1] === "'") {
    return text.slice(1, -1).replace(/''/g, "'");
  }
  return text;
}

function findMatchingRParen(tokens: ReadonlyArray<Token>, lparenIdx: number): number {
  let depth = 0;
  for (let k = lparenIdx; k < tokens.length; k += 1) {
    const t = tokens[k]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') {
      depth -= 1;
      if (depth === 0) return k;
    }
  }
  return -1;
}

function findSemicolonAfter(tokens: ReadonlyArray<Token>, from: number): number {
  for (let k = from; k < tokens.length; k += 1) {
    if (tokens[k]!.type === 'semicolon') return k;
  }
  return -1;
}

function parseColumnList(
  tokens: ReadonlyArray<Token>,
  start: number,
  end: number,
): InBufferTable['columns'] {
  // Split into top-level comma-separated declarations (skipping nested parens).
  const columns: { name: string; dataType: string }[] = [];
  let segStart = start;
  let depth = 0;
  for (let k = start; k < end; k += 1) {
    const t = tokens[k]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) {
      maybeEmitColumn(tokens, segStart, k, columns);
      segStart = k + 1;
    }
  }
  if (segStart < end) {
    maybeEmitColumn(tokens, segStart, end, columns);
  }
  return columns;
}

function maybeEmitColumn(
  tokens: ReadonlyArray<Token>,
  start: number,
  end: number,
  out: { name: string; dataType: string }[],
): void {
  if (start >= end) return;
  const first = tokens[start]!;
  const firstUpper = identUpper(first);
  // Skip table-level constraint clauses.
  if (firstUpper && TABLE_LEVEL_CLAUSE_KEYWORDS.has(firstUpper)) return;
  // Column name is the first ident/kw token in this segment.
  if (first.type !== 'ident' && first.type !== 'kw') return;
  const name = unquoteIdent(first.text);
  // Data type spans from the next token until the first top-level keyword
  // that introduces a column constraint at depth 0, or until segment end.
  const dataType = extractDataType(tokens, start + 1, end);
  out.push({ name, dataType });
}

const COLUMN_OPTION_KEYWORDS = new Set([
  'NOT',
  'NULL',
  'DEFAULT',
  'AUTO_INCREMENT',
  'GENERATED',
  'PRIMARY',
  'UNIQUE',
  'KEY',
  'COMMENT',
  'COLLATE',
  'CHARACTER',
  'ON',
  'REFERENCES',
  'CHECK',
  'STORED',
  'VIRTUAL',
  'INVISIBLE',
  'VISIBLE',
  'AS',
  'CONSTRAINT',
]);

function extractDataType(
  tokens: ReadonlyArray<Token>,
  start: number,
  end: number,
): string {
  let depth = 0;
  let lastIncluded = start;
  for (let k = start; k < end; k += 1) {
    const t = tokens[k]!;
    if (t.type === 'lparen') {
      depth += 1;
      lastIncluded = k;
      continue;
    }
    if (t.type === 'rparen') {
      depth -= 1;
      lastIncluded = k;
      continue;
    }
    if (depth > 0) {
      lastIncluded = k;
      continue;
    }
    const upper = identUpper(t);
    if (upper && COLUMN_OPTION_KEYWORDS.has(upper)) break;
    lastIncluded = k;
  }
  // Render the dataType from the first token through lastIncluded by
  // joining their raw text — preserves parens like `DECIMAL(8,2)`.
  const parts: string[] = [];
  for (let k = start; k <= lastIncluded && k < end; k += 1) {
    parts.push(tokens[k]!.text);
  }
  return joinTokens(parts);
}

function joinTokens(parts: ReadonlyArray<string>): string {
  // Concatenate tokens; insert a space between two consecutive identifier-like
  // tokens to keep e.g. `UNSIGNED INT` readable. We're approximate — the
  // dataType is only displayed as text.
  let acc = '';
  for (const p of parts) {
    if (acc.length === 0) {
      acc = p;
      continue;
    }
    const lastCh = acc[acc.length - 1]!;
    const firstCh = p[0]!;
    if (isIdentChar(lastCh) && isIdentChar(firstCh)) {
      acc += ' ';
    }
    acc += p;
  }
  return acc;
}

function isIdentChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

function unquoteIdent(text: string): string {
  if (text.length >= 2 && text[0] === '`' && text[text.length - 1] === '`') {
    return text.slice(1, -1).replace(/``/g, '`');
  }
  if (text.length >= 2 && text[0] === '"' && text[text.length - 1] === '"') {
    return text.slice(1, -1).replace(/""/g, '"');
  }
  if (text.length >= 2 && text[0] === '[' && text[text.length - 1] === ']') {
    return text.slice(1, -1);
  }
  return text;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return unquoteIdent(t.text).toUpperCase();
}
